import {Box, Grid, Section, SectionIntro, Statistic} from '@primer/react-brand/esm'
import {BrandedDivider} from '../../../components/BrandedDivider'
import {FlexSuiteLargeCards} from '../../../components/FlexSuiteLargeCards'
import {FlexSuiteOutroCTA} from '../../../components/FlexSuiteOutroCTA'
import {FlexSuiteOutroCustomerStories} from '../../../components/FlexSuiteOutroCustomerStories'
import {FlexSuiteOutroResources} from '../../../components/FlexSuiteOutroResources'
import {FlexSuiteTestimonialBanner, FlexSuiteTestimonialQuote} from '../../../components/FlexSuiteTestimonial'
import baseStyles from '../../../styles/base.module.css'
import {renderWithoutParagraphs} from '../../../utils'
import type {OutroSectionProps} from './types'

export function OutroSection({
  settings,
  outroLargeCards,
  outroTestimonial,
  outroTestimonialLink,
  outroTestimonialLogo,
  outroTestimonialQuote,
  outroTestimonialImage,
  outroTestimonialLabel,
  outroTestimonialName,
  outroTestimonialTitle,
  outroStats,
  outroCustomerStories,
  outroCtaHeading,
  outroCtaDescription,
  outroCtaOne,
  outroCtaTwo,
  outroBrandedDivider,
  outroResourcesHeading,
  outroResources,
}: OutroSectionProps) {
  const brand = settings?.fields.brand

  const hasLargeCards = Boolean(outroLargeCards?.length)
  const hasQuoteTestimonial = outroTestimonial === 'Quote' && Boolean(outroTestimonialQuote)
  const hasBrandBannerTestimonial = outroTestimonial === 'Brand Banner' && Boolean(outroTestimonialQuote)
  const hasStats = Boolean(outroStats?.length)
  const hasCustomerStories = outroCustomerStories && outroCustomerStories.length > 0
  const hasResources = Boolean(outroResources?.length)
  const hasCtaBanner = Boolean(
    outroCtaHeading || outroCtaDescription || outroCtaOne?.fields.href || outroCtaTwo?.fields.href,
  )
  const hasBrandedDivider = Boolean(outroBrandedDivider && brand === 'Security')

  if (
    !hasLargeCards &&
    !hasQuoteTestimonial &&
    !hasBrandBannerTestimonial &&
    !hasStats &&
    !hasCustomerStories &&
    !hasCtaBanner &&
    !hasResources &&
    !hasBrandedDivider
  ) {
    return null
  }

  return (
    <Box marginBlockStart={48} className={baseStyles.greenBorderBottom}>
      <Section fullWidth paddingBlockStart="none" paddingBlockEnd="none">
        {hasLargeCards ? (
          <Box
            className={`${baseStyles.gridFrameTop} ${baseStyles.gridFrameBottom}`}
            paddingBlockStart={48}
            paddingBlockEnd={48}
          >
            <FlexSuiteLargeCards cards={outroLargeCards} />
          </Box>
        ) : null}

        {hasQuoteTestimonial ? (
          <Box paddingBlockStart="spacious" paddingBlockEnd="spacious">
            <Grid>
              <Grid.Column>
                <FlexSuiteTestimonialQuote
                  quote={outroTestimonialQuote}
                  name={outroTestimonialName}
                  title={outroTestimonialTitle}
                  link={outroTestimonialLink?.fields}
                  logo={outroTestimonialLogo}
                />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {hasBrandBannerTestimonial ? (
          <Box paddingBlockStart="spacious" paddingBlockEnd="spacious">
            {outroTestimonialLabel ? (
              <Box marginBlockEnd={16}>
                <SectionIntro align="center" fullWidth>
                  <SectionIntro.Label>{outroTestimonialLabel}</SectionIntro.Label>
                </SectionIntro>
              </Box>
            ) : null}
            <FlexSuiteTestimonialBanner
              quote={outroTestimonialQuote}
              link={outroTestimonialLink?.fields}
              logo={outroTestimonialLogo}
              image={outroTestimonialImage}
            />
          </Box>
        ) : null}

        {hasStats ? (
          <Box
            className={`${baseStyles.gridFrameTop} ${baseStyles.gridFrameBottom}`}
            paddingBlockStart={{narrow: 'spacious', regular: 64}}
            paddingBlockEnd={{narrow: 'spacious', regular: 64}}
          >
            <Grid>
              {outroStats?.map(stat => (
                <Grid.Column key={stat.sys.id} span={{medium: 4}}>
                  <Statistic>
                    <Statistic.Heading>{renderWithoutParagraphs(stat.fields.heading)}</Statistic.Heading>
                    <Statistic.Description>{renderWithoutParagraphs(stat.fields.description)}</Statistic.Description>
                  </Statistic>
                </Grid.Column>
              ))}
            </Grid>
          </Box>
        ) : null}

        {hasCustomerStories ? (
          <FlexSuiteOutroCustomerStories variant="icon-cards" stories={outroCustomerStories} />
        ) : null}

        {hasCtaBanner ? (
          <Box paddingBlockStart={{narrow: 'spacious', regular: 64}}>
            <FlexSuiteOutroCTA
              outroCtaHeading={outroCtaHeading}
              outroCtaDescription={outroCtaDescription}
              outroCtaOne={outroCtaOne}
              outroCtaTwo={outroCtaTwo}
            />
          </Box>
        ) : null}

        {hasBrandedDivider ? (
          <Box className={baseStyles.gridFrameBottom} data-testid="category-outro-branded-divider">
            <Grid>
              <Grid.Column>
                <BrandedDivider brand={brand} />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {hasResources ? (
          <FlexSuiteOutroResources outroResourcesHeading={outroResourcesHeading} outroResources={outroResources} />
        ) : null}
      </Section>
    </Box>
  )
}
